import React from 'react';
import { useAppDispatch } from '../../../redux/hooks';

import {
    Button,
    Menu,
    MenuItemLink,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Tooltip,
    makeStyles,
} from '@fluentui/react-components';
import { ChevronDown20Filled } from '@fluentui/react-icons';
import UserSettingsMenu from './UserSettingsMenu';
import proGPTLogo from '../../../assets/progpt-0424-lg-rev.png';
import proGPTSmallLogo from '../../../assets/Microchip-Filled.svg';
import { customColors, customFonts } from '../../../styles';
import { setShowQuickGuide } from '../../../redux/features/commonData/commonDataSlice';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        width: '100%',
        minWidth: 0,
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    titleContainer: {
        display: 'flex',
        flex: 1,
        minWidth: 0,
        alignItems: 'center',
        height: '100%',
    },
    logo: {
        display: 'block',
        height: '60px',
        width: 'auto',
        paddingLeft: '3%',
        '@media (max-width: 1000px)': {
            display: 'none',
        },
    },
    smallLogo: {
        display: 'none',
        height: '32px',
        width: '32px',
        paddingLeft: '8px',
        '@media (max-width: 1000px)': {
            display: 'block',
        },
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
    },
    cornerItems: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '32px',
    },
    gettingStartedButton: {
        textDecorationLine: 'none',
        marginLeft: '5px',
        marginTop: '5px',
        marginBottom: '5px',
        marginRight: '30px',
        paddingLeft: '10px',
        paddingRight: '10px',
        paddingBottom: '5px',
        paddingTop: '5px',
        display: 'block',
        color: '#46596b',
        backgroundColor: customColors.white,
        width: 'max-content',
        fontWeight: '700',
        fontFamily: customFonts.Lato,
        fontSize: '13.5px',
        border: `1px solid ${customColors.protivitiOrange}`,
        borderRadius: '5px',
        ':hover': {
            border: `1px solid ${customColors.protivitiOrange}`,
            color: '#46596b',
            fontWeight: '700',
            fontFamily: customFonts.Lato,
            fontSize: '13.5px',
        },
    },
    submitIssueButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '5px',
        borderRadius: '5px',
        textDecorationLine: 'none',
        marginLeft: '5px',
        marginTop: '5px',
        marginBottom: '5px',
        marginRight: '30px',
        paddingLeft: '10px',
        paddingRight: '5px',
        paddingBottom: '5px',
        paddingTop: '5px',
        color: customColors.buttonTextDarkBlue,
        backgroundColor: customColors.white,
        width: 'max-content',
        fontWeight: '700',
        fontFamily: customFonts.Lato,
        fontSize: '13.5px',
        border: `1px solid ${customColors.protivitiOrange}`,
        ':hover': {
            color: customColors.buttonTextDarkBlue,
            backgroundColor: customColors.white,
            border: `1px solid ${customColors.protivitiOrange}`,
            ':active': {
                color: customColors.buttonTextDarkBlue,
                backgroundColor: customColors.white,
                border: `1px solid ${customColors.protivitiOrange}`,
            },
        },
    },
    submitIssueButtonChevron: {
        stroke: customColors.buttonTextDarkBlue,
        strokeWidth: '0.25px',
    },
    menu: {
        top: '5px !important',
    },
    menuList: {
        gap: '3px',
    },
    menuItem: {
        maxWidth: '350px',
        backgroundColor: 'rgba(0, 63, 104, 0.05)',
        fontFamily: customFonts.Lato,
        fontWeight: 500,
        boxSizing: 'border-box',
        outline: '1.5px solid transparent',
        transition: 'outline-color 0.2s ease, outline-width 0.2s ease',
        '&:hover': {
            outline: `1.5px solid ${customColors.protivitiOrange}`,
        },
    },
});

export interface ChatHeaderProps {
    displayName: string;
    email: string;
    isAdmin: boolean;
    photoUrl?: string;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({ displayName, email, isAdmin, photoUrl }) => {
    const classes = useStyles();
    const dispatch = useAppDispatch();

    return (
        <div className={classes.root}>
            <div className={classes.titleContainer}>
                <img src={proGPTLogo} alt="ProGPT" className={classes.logo} />
                <img src={proGPTSmallLogo} alt="ProGPT" className={classes.smallLogo} />
            </div>
            <div className={classes.actions}>
                <Button className={classes.gettingStartedButton} onClick={() => dispatch(setShowQuickGuide(true))}>
                    Getting Started
                </Button>
                <Menu>
                    <MenuTrigger disableButtonEnhancement>
                        <Button className={classes.submitIssueButton}>
                            <span>Submit Issue</span>
                            <ChevronDown20Filled className={classes.submitIssueButtonChevron} />
                        </Button>
                    </MenuTrigger>

                    <MenuPopover className={classes.menu}>
                        <MenuList className={classes.menuList}>
                            <Tooltip
                                content="Report an issue with ProGPT"
                                relationship="label"
                                positioning="above-start"
                            >
                                <MenuItemLink
                                    href="mailto:ProtivitiAITeam@protiviti.com;technical.support@protiviti.com?subject=ProGPT%20Issue"
                                    target="_blank"
                                    rel="noreferrer"
                                    className={classes.menuItem}
                                >
                                    Submit Issue
                                </MenuItemLink>
                            </Tooltip>
                            <Tooltip
                                content="Provide feedback about ProGPT"
                                relationship="label"
                                positioning="below-start"
                            >
                                <MenuItemLink
                                    href="mailto:ProtivitiAITeam@protiviti.com;technical.support@protiviti.com?subject=ProGPT%20Feedback"
                                    target="_blank"
                                    rel="noreferrer"
                                    className={classes.menuItem}
                                >
                                    Submit Feedback
                                </MenuItemLink>
                            </Tooltip>
                        </MenuList>
                    </MenuPopover>
                </Menu>
                <div className={classes.cornerItems}>
                    <UserSettingsMenu displayName={displayName} email={email} isAdmin={isAdmin} photoUrl={photoUrl} />
                </div>
            </div>
        </div>
    );
};

export default ChatHeader;
